// The node's own estimate (Node Control's Radar rich data, Radar analysis).
// Firmware .61 can estimate the level from every beat signal it captures
// (radar_edge 1, 2 with a timing benchmark: the strongest echo's Hann peak);
// firmware .62 runs estimator v2's signal processing on the node (radar_edge
// 3, 4 with its benchmark: the leading edge, and the 4 strongest echoes in
// its gate so the server can decide from them) and can hold a rich wake's
// raw captures back (the hybrid upload: radar_keep_every, radar_keep_mm,
// radar_keep_db). The node searches radar_gate_min_mm to radar_gate_max_mm;
// the radar profile suggests the gate that matches the server's
// (suggested_gate) and keeps the node's own search window (near_mm,
// max_water_mm). The server is the authority: these helpers read it
// defensively and word it. Pure: no React, network or storage.
import {
  EDGE_NAMES,
  edgeSettingsText,
  finite,
  formatMm as mm,
  keepRuleText,
  ordinal,
} from "utils/radarDiagnostics";
import { readWhole } from "utils/radarBurst";

export { EDGE_NAMES, edgeSettingsText, keepRuleText };

export const EDGE_KEY = "radar_edge";
export const GATE_KEYS = ["radar_gate_min_mm", "radar_gate_max_mm"];
export const KEEP_KEYS = ["radar_keep_every", "radar_keep_mm", "radar_keep_db"];
// The card's section sends these; the projection reads the estimate and the
// hybrid upload (not the gate).
export const EDGE_KEYS = [EDGE_KEY, ...GATE_KEYS, ...KEEP_KEYS];
export const EDGE_PLAN_KEYS = [EDGE_KEY, ...KEEP_KEYS];
// The firmware's limits, for a server that sends no rules.
export const EDGE_RULES = {
  radar_edge: [0, 4],
  radar_gate_min_mm: [0, 8000],
  radar_gate_max_mm: [0, 8000],
  radar_keep_every: [1, 144],
  radar_keep_mm: [0, 1000],
  radar_keep_db: [0, 30],
};
// The node's own defaults: no estimate, its gate 300-1,300 mm, every rich
// wake's raw captures sent.
export const EDGE_DEFAULTS = {
  edge: 0,
  gateMin: 300,
  gateMax: 1300,
  every: 1,
  mm: 0,
  db: 0,
};
// Estimator v2 on the node; the hybrid upload needs it (3 or 4).
export const EDGE_RECOMMENDED = 3;
export const KEEP_EDGES = [3, 4];
// The firmware build each estimate needs.
export const EDGE_BUILDS = { 1: 61, 2: 61, 3: 62, 4: 62 };
// The choice on Node Control: [radar_edge, name, what it does and costs].
// The bytes are the firmware's examples (a beat-signal capture's message and
// its wake's reading carry the estimate); the battery a day is under Before
// you apply.
export const EDGE_CHOICES = [
  [0, "Off", "The node sends no estimate of its own."],
  [
    1,
    "Peak (v1)",
    "The strongest echo's peak in each beat signal (firmware rtct.61): about 0.07 kB more in each beat-signal capture and in its reading.",
  ],
  [
    2,
    "Peak + timing",
    "Peak (v1) with ten timed runs and the portable FFT's answer beside it: about 0.14 kB; for bench tests.",
  ],
  [
    3,
    "Leading edge (v2, recommended)",
    "Estimator v2's leading edge and the 4 strongest echoes in the gate (firmware rtct.62): about 0.25 kB more in each beat-signal capture and in its reading. The server can decide from those echoes, so the node can hold raw captures back (Raw uploads, below).",
  ],
  [
    4,
    "Leading edge + timing",
    "Leading edge (v2) with its timing benchmark: about 0.32 kB; for bench tests.",
  ],
];

// The timing builds, for bench tests: offered behind the card's "Bench-test
// options".
export const EDGE_BENCH = [2, 4];

// The choices in a few words, for the card's state.
export const EDGE_SHORT = {
  0: "off",
  1: "peak (v1)",
  2: "peak + timing",
  3: "leading edge (v2)",
  4: "leading edge + timing",
};

// The server's [low, high] for a key, else the firmware's.
export const edgeRule = (rules, key) =>
  Array.isArray(rules?.[key]) && rules[key].length === 2
    ? rules[key]
    : EDGE_RULES[key];

// The settings as the node reports them: {edge, gateMin, gateMax, every, mm,
// db}, null where not reported or out of range.
export function readEdge(reported, rules = null) {
  const read = (key) => readWhole(reported?.[key], edgeRule(rules, key));
  return {
    edge: read("radar_edge"),
    gateMin: read("radar_gate_min_mm"),
    gateMax: read("radar_gate_max_mm"),
    every: read("radar_keep_every"),
    mm: read("radar_keep_mm"),
    db: read("radar_keep_db"),
  };
}

// The hybrid upload holds wakes back: estimator v2 on the node and raw
// captures on fewer than every rich wake.
export const keepHolds = ({ edge, every }) =>
  KEEP_EDGES.includes(edge) && Number.isInteger(every) && every > 1;

// "Leading edge (v2)", "Off".
export const edgeName = (edge) => {
  const words = EDGE_NAMES[edge];
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "Not set";
};

// Why a firmware cannot give an estimate: radar_edge 1-2 need rtct.61, 3-4
// rtct.62 (rtct.61 reads 3 and 4 as damaged and reads back 0). `build`: the
// node's rtct build (rtctBuild()), null when unknown. -> words or null.
export function edgeFirmwareNote(edge, build) {
  const need = EDGE_BUILDS[edge];
  if (!need || build === null || build === undefined || build >= need)
    return null;
  return `${edgeName(edge)} needs firmware rtct.${need} or later; the node runs rtct.${build}${need === 62 && build === 61 ? ", which reads it as damaged and reports 0" : ", which ignores it"}.`;
}

// ---- The hybrid upload's note on a rich wake (firmware .62) ----

// A reading's keep {q 1 queued / 0 held back, why}: {queued, why} or null.
export function readKeep(value) {
  if (!value || typeof value !== "object") return null;
  const q = value.q === 1 ? 1 : value.q === 0 ? 0 : null;
  if (q === null) return null;
  const why =
    typeof value.why === "string" && value.why.trim()
      ? value.why.trim().toLowerCase()
      : null;
  return { queued: q === 1, why };
}

// Why the node sent a rich wake's raw captures, or held them back, in words:
// `rules`, the node's keep settings ({every, mm, db}: readEdge()'s), name
// the numbers where known. short: a few words for the burst list ("raw sent:
// every 6th wake", "held back on the node"); else a sentence. -> words or
// null without a note.
export function keepNoteText(keep, rules = {}, { short = false } = {}) {
  const k = readKeep(keep);
  if (!k) return null;
  const every = finite(rules?.every);
  const move = finite(rules?.mm);
  const near = finite(rules?.db);
  if (!k.queued)
    return short
      ? "held back on the node"
      : "Held back on the node: only its reading came, with the on-node estimate and its echo list, and the server decided its level from those.";
  const why = {
    every: short
      ? every > 1
        ? `every ${ordinal(every)} wake`
        : "its turn"
      : every > 1
        ? `every ${ordinal(every)} rich wake sends its raw captures`
        : "its turn: every Nth rich wake sends its raw captures",
    mm: short
      ? move > 0
        ? `moved over ${move.toLocaleString()} mm`
        : "the estimate moved"
      : `the node's estimate moved more than ${move > 0 ? mm(move) : "its limit"} from the last one sent`,
    db: short
      ? near > 0
        ? `runner-up within ${near.toLocaleString()} dB`
        : "a close runner-up"
      : `the runner-up echo stood within ${near > 0 ? `${near.toLocaleString()} dB` : "its limit"} of the strongest, allowing for range`,
    none: short ? "no echo in the gate" : "no echo stood in the node's gate",
    clip: short ? "clipped" : "the beat signal clipped",
    est: short ? "no estimate" : "the node had no estimate of it",
  }[k.why];
  if (short) return `raw sent: ${why || k.why || "queued"}`;
  return `Sent: ${why || (k.why ? `the node's reason "${k.why}"` : "queued by the node")}.`;
}

// ---- The gate the server suggests (radar/profile/'s suggested_gate) ----

const pair = (v) =>
  Array.isArray(v) && v.length === 2 && v.every((x) => finite(x) !== null)
    ? v.map(Number)
    : null;
// {min, max (the whole millimetres to push), gateMm ([lo, hi]: estimator
// v2's gate on the server), bins ([first, last] zoom bin), stepMm, calMm,
// basis ("profile" | "default"), node ({min, max}: the gate the node last
// read back, null until it does), matches (the node's gives the same bins;
// null until read back)} or null (none, or a server before it).
export function readSuggestedGate(data) {
  const g = data?.suggested_gate;
  if (!g || typeof g !== "object") return null;
  const min = readWhole(g.radar_gate_min_mm, EDGE_RULES.radar_gate_min_mm);
  const max = readWhole(g.radar_gate_max_mm, EDGE_RULES.radar_gate_max_mm);
  if (min === null || max === null || min >= max) return null;
  const nodeMin = readWhole(
    g.node?.radar_gate_min_mm,
    EDGE_RULES.radar_gate_min_mm,
  );
  const nodeMax = readWhole(
    g.node?.radar_gate_max_mm,
    EDGE_RULES.radar_gate_max_mm,
  );
  return {
    min,
    max,
    gateMm: pair(g.gate_mm),
    bins: pair(g.bins),
    stepMm: finite(g.step_mm),
    calMm: finite(g.cal_mm),
    basis:
      g.basis === "profile"
        ? "profile"
        : g.basis === "default"
          ? "default"
          : null,
    node:
      nodeMin !== null && nodeMax !== null
        ? { min: nodeMin, max: nodeMax }
        : null,
    matches: typeof g.matches === "boolean" ? g.matches : null,
  };
}

// "270–1,208 mm".
export const gateText = (min, max) =>
  `${Number(min).toLocaleString()}–${mm(max)}`;

// What the suggestion rests on: "From this node's radar profile (its floor
// and search window): the server looks for the level between 270 and
// 1,208.2 mm, and this gate gives the node's estimate the same echoes to
// choose from (zoom bins 147–622)."
export function gateBasisText(gate) {
  if (!gate) return "";
  const basis =
    gate.basis === "profile"
      ? "From this node's radar profile (its floor and search window)"
      : gate.basis === "default"
        ? "From a 1.2 m pipe: this node has no radar profile yet (commission it on Radar analysis)"
        : null;
  const server = gate.gateMm
    ? `the server looks for the level between ${Number(gate.gateMm[0]).toLocaleString(undefined, { maximumFractionDigits: 1 })} and ${mm(gate.gateMm[1], 1)}, and this gate gives the node's estimate the same echoes to choose from${gate.bins ? ` (zoom bins ${gate.bins[0]}–${gate.bins[1]})` : ""}`
    : null;
  if (!basis && !server) return "";
  if (!basis) return `${server.charAt(0).toUpperCase()}${server.slice(1)}.`;
  return `${basis}${server ? `: ${server}` : ""}.`;
}

// Whether the node's gate is the suggested one, in words; null without a
// suggestion.
export function gateMatchText(gate) {
  if (!gate) return null;
  if (gate.matches === true)
    return `The node's gate${gate.node ? `, ${gateText(gate.node.min, gate.node.max)},` : ""} already gives the same bins.`;
  if (gate.matches === false)
    return `The node searches ${gate.node ? `${gateText(gate.node.min, gate.node.max)} now` : "another gate"}: not the server's, so its echoes can differ from ours.`;
  return "The node has not reported its gate yet; it searches 300–1,300 mm until told otherwise.";
}

// ---- The node's estimate against ours (analysis edge2_mm, edge2_diff_mm) ----

// The node's answer and the server's leading edge of the same capture agree
// within a rounding when both chose the same echo, and differ by tens of mm
// when the server's map, floor rule or tracker chose another.
export const NODE_AGREE_MM = 1;
// -> {tone: "agree" | "disagree" | "none", text} or null without an estimate
// of the node's.
export function nodeAgreement(edge2Mm, diffMm, n = null) {
  const node = finite(edge2Mm);
  const diff = finite(diffMm);
  const count = finite(n);
  if (node === null && diff === null) return null;
  const of =
    count !== null && count > 0
      ? ` (${count.toLocaleString()} beat signal${count === 1 ? "" : "s"})`
      : "";
  if (diff === null)
    return {
      tone: "none",
      text: `The on-node estimate${of}: nothing of ours on the same capture to compare it with.`,
    };
  const apart = Math.abs(diff);
  if (apart < NODE_AGREE_MM)
    return {
      tone: "agree",
      text: `The node and the server chose the same echo${of}: ${apart < 0.05 ? "within 0.05 mm" : `${mm(apart, 2)} apart`}.`,
    };
  return {
    tone: "disagree",
    text: `The node's answer is ${mm(apart, 1)} ${diff > 0 ? "farther from the radar than" : "nearer the radar than"} ours${of}: the server's empty-pipe map, floor rule or tracker chose another of the node's echoes.`,
  };
}

// ---- The node's own search window (radar/profile/'s near_mm, max_water_mm) ----

export const NEAR_MM = [0, 2000];
export const MAX_WATER_MM = [10, 5000];
// The global setting, unless the server's RTCT_ESTIMATOR_V2 changes it.
export const NEAR_DEFAULT_MM = 270;
export const DEFAULT_PIPE_MM = 1200;
// How far beyond the floor the server's gate ends, by default.
export const FLOOR_MARGIN_PCT = 3;

// The window a profile keeps: {nearMm, maxWaterMm (null: the global
// setting), kept (the server keeps one: its profile has the keys)}.
export function windowOf(profile) {
  const has = (key) =>
    !!profile && Object.prototype.hasOwnProperty.call(profile, key);
  return {
    nearMm: finite(profile?.near_mm),
    maxWaterMm: finite(profile?.max_water_mm),
    kept: has("near_mm") || has("max_water_mm"),
  };
}

// "search from 300 mm, deepest water 900 mm"; "" for the global window.
export function windowText({ nearMm = null, maxWaterMm = null } = {}) {
  return [
    nearMm !== null ? `search from ${mm(nearMm)}` : null,
    maxWaterMm !== null ? `deepest water ${mm(maxWaterMm)}` : null,
  ]
    .filter(Boolean)
    .join(", ");
}

// What Save sends for the window: whole millimetres, a blank for the global
// setting (null). -> {body: {near_mm, max_water_mm}} or {error}
export function windowPayload({ nearText = "", maxText = "" } = {}) {
  const read = (text, name, [lo, hi], blank) => {
    const s = String(text ?? "").trim();
    if (!s) return { value: null };
    if (!/^\d+$/.test(s) || Number(s) < lo || Number(s) > hi)
      return {
        error: `${name}: whole millimetres from ${lo} to ${hi.toLocaleString()}, or leave it blank for ${blank}.`,
      };
    return { value: Number(s) };
  };
  const near = read(nearText, "Search from", NEAR_MM, "the global setting");
  const deep = read(maxText, "Deepest water", MAX_WATER_MM, "no limit");
  if (near.error || deep.error) return { error: near.error || deep.error };
  return { body: { near_mm: near.value, max_water_mm: deep.value } };
}

// The floor the server's gate goes by: the expected floor, else the pipe's
// length, else 1.2 m.
export const gateFloorMm = (profile) =>
  finite(profile?.expected_floor_mm) ??
  finite(profile?.pipe_length_mm) ??
  DEFAULT_PIPE_MM;

// The search gate a window gives, as the server works it out: from near_mm
// (or the global setting), but no nearer than the floor less max_water_mm,
// to the end of the server's gate (`endMm`, suggested_gate's; else the floor
// plus its usual margin). -> {from, to, empty}
export function windowGate({
  nearMm = null,
  maxWaterMm = null,
  floorMm = DEFAULT_PIPE_MM,
  endMm = null,
  globalNearMm = NEAR_DEFAULT_MM,
} = {}) {
  const from = Math.max(
    nearMm ?? globalNearMm,
    maxWaterMm ? floorMm - maxWaterMm : -Infinity,
  );
  const to = endMm ?? floorMm * (1 + FLOOR_MARGIN_PCT / 100);
  return { from, to, empty: !(from < to) };
}

// The global setting near_mm stands for: the start of the server's gate when
// the profile sets neither end of the window, else the usual 270 mm.
export function globalNearMm(profile, gate) {
  const w = windowOf(profile);
  return w.nearMm === null && w.maxWaterMm === null && gate?.gateMm
    ? gate.gateMm[0]
    : NEAR_DEFAULT_MM;
}

// ---- What the hybrid upload saves (the projection's rich line) ----

// The rich line's figures for the card: battery a day, capture messages sent
// a day, data a month, the captures and wakes a day, the estimate's own
// charge and the hybrid upload ({every, mm, db, share, shareSource,
// readings}, null while off). Null without a line.
export function keepFigures(line) {
  if (!line || line.off) return null;
  return {
    mahDay: line.mahDay ? line.mahDay.high : null,
    uploadsDay: finite(line.uploadsDay),
    mbMonth: line.mbMonth ? line.mbMonth.high : null,
    capturesDay: finite(line.capturesDay),
    wakesDay: finite(line.wakesDay),
    edge: finite(line.edge),
    edgeMahDay: finite(line.edgeMahDay),
    keep: line.keep || null,
  };
}

// The share of rich wakes whose raw captures go up, in words: "1 in 6,
// modelled (the wakes its other rules send come on top)", "19.7 %, measured
// on its last 812 rich wakes".
export function keepShareText(keep) {
  if (!keep || finite(keep.share) === null) return null;
  const share = finite(keep.share);
  if (keep.shareSource === "measured")
    return `${(share * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })} %, measured on its last ${(finite(keep.readings) ?? 0).toLocaleString()} rich wakes`;
  const every = finite(keep.every);
  return `${every > 1 ? `1 in ${every.toLocaleString()}` : `${Math.round(share * 100)} %`}, modelled (the wakes its other rules send come on top)`;
}
